import React, { useState } from 'react';
import { Monitor, Cpu, Search, Mail } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { computerSales } from '../mock';

const Shop = () => {
  const { toast } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');

  const filteredProducts = computerSales.filter((product) => {
    const matchesSearch = product.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         product.brand.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || product.category.toLowerCase() === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  const [quoteProduct, setQuoteProduct] = useState(null);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteFormData, setQuoteFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: ''
  });

  const handleContactForPricing = (product) => {
    setQuoteProduct(product);
    setShowQuoteForm(true);
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/quote-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          brand: quoteProduct.brand,
          model: quoteProduct.model,
          category: quoteProduct.category,
          specs: quoteProduct.specs,
          ...quoteFormData
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        toast({
          title: "Quote Request Submitted!",
          description: data.message || `We'll contact you shortly about ${quoteProduct.brand} ${quoteProduct.model}.`,
        });
        setShowQuoteForm(false);
        setQuoteFormData({ customerName: '', customerEmail: '', customerPhone: '' });
      } else {
        throw new Error(data.detail || 'Failed to submit quote request');
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to submit request. Please try again or call us directly.",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section with Image */}
      <section className="relative py-16 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1575491825092-7431433a2cbd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHw0fHxtb2Rlcm4lMjBsYXB0b3B8ZW58MHx8fHwxNzYwNTczMTQ2fDA&ixlib=rb-4.1.0&q=85"
            alt="Modern Laptops"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 to-slate-700/85"></div>
        </div>

        <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl text-white">
            <Badge className="bg-blue-500 text-white hover:bg-blue-600 mb-4">
              Premium Selection
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">Shop Computers & Laptops</h1>
            <p className="text-xl text-gray-200">
              Browse our curated selection of high-performance computers and laptops from leading brands.
            </p>
          </div>
        </div>
      </section>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <Input
                  placeholder="Search by brand or model..."
                  className="pl-10"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="laptop">Laptops</SelectItem>
                  <SelectItem value="desktop">Desktops</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProducts.map((product) => (
            <Card key={product.id} className="overflow-hidden hover:shadow-xl transition-all duration-300 group hover:-translate-y-1">
              <div className="relative h-64 overflow-hidden bg-gray-100">
                <img
                  src={product.image}
                  alt={`${product.brand} ${product.model}`}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <Badge className="absolute top-4 right-4 bg-blue-600 text-white">{product.category}</Badge>
              </div>
              
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardDescription className="text-xs mb-1">{product.brand}</CardDescription>
                    <CardTitle className="text-xl">{product.model}</CardTitle>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-start text-sm text-gray-600">
                    <Cpu className="h-4 w-4 mr-2 text-blue-600 mt-0.5 flex-shrink-0" />
                    <span className="text-xs">{product.specs}</span>
                  </div>
                </div>
              </CardContent>
              
              <CardFooter className="flex gap-2">
                <Button className="flex-1" onClick={() => handleContactForPricing(product)}>
                  <Mail className="h-4 w-4 mr-2" />
                  Contact for Pricing
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>

        {filteredProducts.length === 0 && (
          <div className="text-center py-16">
            <Monitor className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No products found</h3>
            <p className="text-gray-600">Try adjusting your search or filter criteria</p>
          </div>
        )}

        {/* Why Buy From Us */}
        <section className="mt-20 bg-white rounded-lg shadow-sm p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Why Buy From Microworld Solution?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Monitor className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Premium Quality</h3>
              <p className="text-gray-600 text-sm">All products are carefully selected and tested for quality assurance.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Cpu className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Free Setup Support</h3>
              <p className="text-gray-600 text-sm">Purchase includes free initial setup and configuration assistance.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-lg mb-2">Extended Warranty</h3>
              <p className="text-gray-600 text-sm">Optional extended warranty plans available for added peace of mind.</p>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="mt-12 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-lg p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Need Help Choosing?</h3>
          <p className="mb-6 text-blue-100">Our experts are here to help you find the perfect computer for your needs.</p>
          <Button size="lg" variant="secondary">
            <Mail className="h-5 w-5 mr-2" />
            Contact Our Sales Team
          </Button>
        </section>
      </div>

      {/* Quote Request Dialog */}
      <Dialog open={showQuoteForm} onOpenChange={setShowQuoteForm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Pricing Quote</DialogTitle>
            <DialogDescription>
              {quoteProduct && `${quoteProduct.brand} ${quoteProduct.model}`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleQuoteSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="customerName">Your Name</Label>
              <Input
                id="customerName"
                placeholder="John Doe"
                value={quoteFormData.customerName}
                onChange={(e) => setQuoteFormData({ ...quoteFormData, customerName: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customerEmail">Email Address</Label>
              <Input
                id="customerEmail"
                type="email"
                placeholder="john@example.com"
                value={quoteFormData.customerEmail}
                onChange={(e) => setQuoteFormData({ ...quoteFormData, customerEmail: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="customerPhone">Phone Number</Label>
              <Input
                id="customerPhone"
                type="tel"
                placeholder="(555) 123-4567"
                value={quoteFormData.customerPhone}
                onChange={(e) => setQuoteFormData({ ...quoteFormData, customerPhone: e.target.value })}
                required
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowQuoteForm(false)}>
                Cancel
              </Button>
              <Button type="submit">Submit Request</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Shop;